import { PropTypes } from 'typed/imports';
import { Component, ReactElement } from 'react';
import { addLocaleData, IntlProvider, FormattedMessage } from 'react-intl';
import { toArray, deepEqual } from 'typed/utils';
import * as en from 'react-intl/locale-data/en';
import * as fi from 'react-intl/locale-data/fi';
import * as sv from 'react-intl/locale-data/sv';

// TODO find how to get needed data for current locale without bundling all of available datas
addLocaleData([...en]);
addLocaleData([...fi]);
addLocaleData([...sv]);

export type Resource = string | { id: string; values?: { [key: string]: any }; };

export const isResource = (value: any): value is Resource => typeof value === 'string' || typeof value === 'object' && value && typeof value.id === 'string';

export const resourcePropType = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
        id: PropTypes.string.isRequired,
        values: PropTypes.object
    })]);

class KeyProvider extends Component<{ children: ReactElement }> {
    render() {
        return this.props.children;
    }
}

interface Props {
    resource: Resource;
    values?: { [key: string]: any };
    tagName?: string;
    className?: string;
};

export default class FormattedResource extends Component<Props> {
    static readonly isResource = isResource;

    static readonly resourcePropType = resourcePropType;

    static readonly equal = deepEqual;

    static readonly propTypes = {
        resource: resourcePropType.isRequired,
        values: PropTypes.object,
        tagName: PropTypes.string,
        className: PropTypes.string
    };

    static readonly contextTypes = { intl: PropTypes.object };


    static readonly defaultProps = {
        tagName: 'span',
        className: ''
    };

    shouldComponentUpdate(nextProps: Props) {
        return !deepEqual(this.props.resource, nextProps.resource) ||
            !deepEqual(this.props.values, nextProps.values) ||
            this.props.tagName !== nextProps.tagName ||
            this.props.className !== nextProps.className;
    }

    _onRenderWithOriginalProvider = (...children: React.ReactNode[]) => {
        return (
            <IntlProvider {...this.context.intl}>
                {this._onRender(...children)}
            </IntlProvider>
        );
    }

    _onRender = (...children: React.ReactNode[]) => {
        const Tag = this.props.tagName!;

        return (
            // @ts-ignore https://github.com/Microsoft/TypeScript/issues/28892
            <Tag className={`formatted-resource ${this.props.className}`}>
                {
                    // suppress react warnings about key uniqueness
                    // if there are substitutable values in resource, FormattedMessage returns an array consisting of parts of string interspersed with substituted values
                    // and if some value is React element it will cause "each array key blah" warning polluting console with fake warnings
                    // but resources should almost never change and even if they do it means we should render completely new resource, not rearrange parts of old one
                    // and we can not change key of element passed from outside
                    // so we wrap it into fake component whose only purpose is to have a key and render its child as is
                    !DEBUG || !children || !Array.isArray(children) ?
                        children :// in release version warnings are disabled so for performance we can omit this workaround
                        toArray(children).map((child, index) => child && (child as ReactElement).type ? <KeyProvider key={index}>{child as ReactElement}</KeyProvider> : child)
                }
            </Tag>
        );
    }

    render() {
        if (!this.props.resource) {
            return this._onRender(null);
        } else if (typeof this.props.resource === 'string' && this.props.values) {
            return (
                <IntlProvider locale={this.context.intl.locale} messages={{ 'scoped_message': this.props.resource || '' }}>
                    <FormattedMessage id="scoped_message" values={this.props.values}>
                        {this._onRenderWithOriginalProvider}
                    </FormattedMessage>
                </IntlProvider>
            );
        }
        else if (typeof this.props.resource === 'string') {
            return this._onRender(this.props.resource);
        } else if (typeof this.props.resource === 'object' && this.props.resource.id) {
            return (
                <FormattedMessage id={this.props.resource.id} values={{ ... this.props.resource.values, ...this.props.values }}>
                    {this._onRender}
                </FormattedMessage>
            );
        } else {
            return this._onRender(null);
        }
    }
}
